import { Text, TextInput, useClickOutside } from "@zapier/design-system";
import { Node } from "@xyflow/react";
import { useState } from "react";
import classNames from "classnames";
import {
  DefaultNodeData,
  NodeType,
  determineAssetType,
} from "@zapier/canvas-state";

import useUpdateNode from "../../../hooks/useUpdateNode";
import { getDefaultColorForAssetType } from "../../../utils/getDefaultColorForAssetType";
import { ColorPicker } from "../../ColorPicker";
import { HexInput } from "../../HexInput";

import styles from "./SidebarColorPicker.module.css";

export function SidebarColorPicker({ node }: { node: Node<DefaultNodeData> }) {
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const toggleColorPicker = () => setIsColorPickerOpen((prev) => !prev);
  const updateNodeAndSave = useUpdateNode();

  const assetType = determineAssetType(
    node.data.services,
    node.type as NodeType,
  );

  const assetNodeDefaultColor = getDefaultColorForAssetType(assetType);
  const defaultColor = assetNodeDefaultColor;

  function resetToDefault() {
    updateNodeAndSave({
      id: node.id,
      data: {
        color: defaultColor,
      },
    });
    setIsColorPickerOpen(false);
  }

  const nodeColor = node.data.color || defaultColor;

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => setIsColorPickerOpen(false),
  });

  return (
    <div ref={setClickOutsideNode} className={styles.colorPickerWrapper}>
      <span className={styles.resetWrapper}>
        <span className={styles.field}>
          <Text type="SmallPrint2">Color</Text>
        </span>
        <button className={styles.reset} onClick={resetToDefault}>
          Reset to default
        </button>
      </span>
      <span className={styles.colorPickerInputWrapper}>
        <button
          className={styles.colorPickerButton}
          onClick={toggleColorPicker}
        >
          <div
            className={classNames(
              styles.colorCircle,
              styles.colorCircleSelected,
            )}
            style={{ backgroundColor: nodeColor }}
          ></div>
        </button>
        <HexInput id={node.id} color={nodeColor}>
          {(inputProps) => (
            <div className={styles.hexInputWrapper}>
              <TextInput {...inputProps} layout="inline" prependContent="#" />
            </div>
          )}
        </HexInput>
      </span>
      {isColorPickerOpen && (
        <div className={styles.colorPickerPanel}>
          <ColorPicker id={node.id} color={nodeColor} showHexInput={false} />
        </div>
      )}
    </div>
  );
}
